import React, { useState, lazy, Suspense, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { balanceApi } from '../../../api/balance';
import { BalanceResponse } from '../../../types/balance.types';
import {
  LayoutGrid,
  Search,
  LogOut,
  Menu,
  MessageSquare,
  AlertTriangle,
  Plus,
  CreditCard,
  User,
  ChevronDown,
  Settings,
  Clock,
  Mail,
  Wrench
} from 'lucide-react';
import BackgroundJobPill from './BackgroundJobPill';
import CompletionNotification from '../../common/CompletionNotification/CompletionNotification';
import DMUnlockCompletionToast from '../../common/DMUnlockCompletionToast/DMUnlockCompletionToast';
import { useAppSelector } from '../../../hooks/reduxHooks';
import {
  selectActiveBulkBatch,
} from '../../../store/slices/dmUnlockSlice';

const FeedbackModal = lazy(() => import('./FeedbackModal'));
const EmailVerificationModal = lazy(() => import('../../results/EmailVerificationModal/EmailVerificationModal'));
const BulkDMUnlockModal = lazy(() => import('../../results/BulkDMUnlockModal/BulkDMUnlockModal'));

interface HeaderProps {
  onMenuClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const searchId = (id && /^\d+$/.test(id)) ? parseInt(id, 10) : undefined;
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackType, setFeedbackType] = useState<'feedback' | 'bug'>('feedback');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showFeedbackDropdown, setShowFeedbackDropdown] = useState(false);
  const [balance, setBalance] = useState<BalanceResponse | null>(null);
  const [previousBalance, setPreviousBalance] = useState<BalanceResponse | null>(null);
  const [loadingBalance, setLoadingBalance] = useState(true);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [isDMUnlockReopenOpen, setIsDMUnlockReopenOpen] = useState(false);
  const [reopenDMUnlockBatchId, setReopenDMUnlockBatchId] = useState<string | null>(null);
  const [showToolsDropdown, setShowToolsDropdown] = useState(false);
  const { completedVerification } = useAppSelector((state) => state.emailVerification);
  const activeBulkBatch = useAppSelector(selectActiveBulkBatch);
  const dmUnlockPersonIds = activeBulkBatch?.personIds ?? [];
  const dmUnlockSearchId = activeBulkBatch?.searchId ?? null;
  
  useEffect(() => {
    if (isAuthenticated) {
      loadBalance();
      const interval = setInterval(() => loadBalance(), 60000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const lastBalanceRefreshKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!completedVerification || balance === null) return;
    const refreshKey = `${completedVerification.status}:${completedVerification.summary}:${completedVerification.credits_charged}`;
    if (lastBalanceRefreshKeyRef.current === refreshKey) return;
    lastBalanceRefreshKeyRef.current = refreshKey;
    loadBalance(true);
  }, [completedVerification]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced balance refresh for DM unlock completions to coalesce rapid multi-batch completions.
  const recentCompletionsCount = useAppSelector((state) => state.dmUnlock.recentCompletions.length);
  const balanceDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevCompletionsCountRef = useRef(0);
  useEffect(() => {
    if (recentCompletionsCount > prevCompletionsCountRef.current && balance !== null) {
      if (balanceDebounceRef.current) clearTimeout(balanceDebounceRef.current);
      balanceDebounceRef.current = setTimeout(() => loadBalance(true), 1500);
    }
    prevCompletionsCountRef.current = recentCompletionsCount;
  }, [recentCompletionsCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBalance = async (showDelta = false) => {
    try {
      const data = await balanceApi.getBalance();

      if (showDelta && balance !== null) {
        setPreviousBalance(balance);
        setTimeout(() => {
          setPreviousBalance(null);
        }, 8000);
      }

      setBalance(data);
    } catch (error) {
      console.error('Failed to load balance:', error);
    } finally {
      setLoadingBalance(false);
    }
  };
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const openFeedbackModal = (type: 'feedback' | 'bug') => {
    setFeedbackType(type);
    setShowFeedbackModal(true);
    setShowFeedbackDropdown(false);
  };

  const openDMUnlockFromHeader = () => {
    if (!activeBulkBatch) return;
    setReopenDMUnlockBatchId(activeBulkBatch.batchId);
    setIsDMUnlockReopenOpen(true);
  };

  const closeDMUnlockFromHeader = () => {
    setIsDMUnlockReopenOpen(false);
    setReopenDMUnlockBatchId(null);
  };

  const handleBalanceClick = () => {
    navigate('/subscriptions');
  };

  const balanceTooltip = balance
    ? [
        `${balance.searches_remaining} searches remaining`,
        `${balance.dm_unlocks_remaining} people remaining`,
        `${balance.email_verifications_remaining} verifies remaining`,
        'You only use a lookup when we find an email. Looking is always free.',
      ].join('\n')
    : undefined;
  
  return (
    <>
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              {isAuthenticated && (
                <button
                  type="button"
                  className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 md:hidden hover:text-primary hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
                  onClick={onMenuClick}
                >
                  <span className="sr-only">Open menu</span>
                  <Menu className="h-6 w-6" />
                </button>
              )}
              <div className="flex-shrink-0 flex items-center ml-2 md:ml-0">
                <Link to="/" className="text-lg md:text-2xl font-bold text-primary">
                  WebLeads
                </Link>
              </div>

              {!isAuthenticated && (
                <div
                  className="relative ml-4"
                  onMouseEnter={() => setShowToolsDropdown(true)}
                  onMouseLeave={() => setShowToolsDropdown(false)}
                >
                  <button
                    onClick={() => setShowToolsDropdown(!showToolsDropdown)}
                    className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-700 hover:text-primary focus:outline-none"
                  >
                    <Wrench className="h-5 w-5 mr-1 text-gray-500" />
                    <span className="hidden sm:inline">Tools</span>
                    <ChevronDown className="w-4 h-4 ml-1 text-gray-500" />
                  </button>

                  {showToolsDropdown && (
                    <div className="absolute left-0 top-full pt-2 w-64 z-50">
                      <div className="bg-white rounded-md shadow-lg py-1 border border-gray-200">
                        <Link
                          to="/comparison-tool"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setShowToolsDropdown(false)}
                        >
                          <LayoutGrid className="h-4 w-4 mr-2 text-gray-500" />
                          Lead Source Comparison (Free)
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {isAuthenticated && (
                <nav className="hidden md:ml-6 md:flex md:space-x-8">
                  <Link 
                    to="/dashboard" 
                    className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-700 hover:text-primary"
                  >
                    <LayoutGrid className="h-5 w-5 mr-1 text-gray-500 group-hover:text-primary" />
                    Dashboard
                  </Link>
                  <Link 
                    to="/searches" 
                    className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-700 hover:text-primary"
                  >
                    <Search className="h-5 w-5 mr-1 text-gray-500 group-hover:text-primary" />
                    Searches
                  </Link>
                  <Link 
                    to="/searches/new" 
                    className="inline-flex items-center px-1 pt-1 text-sm font-bold text-primary hover:text-primary/90"
                  >
                     <Plus className="h-5 w-5 mr-1" />
                    New Search
                  </Link>
                </nav>
              )}
            </div>
            
            <div className="flex items-center space-x-2 md:space-x-4">
              {isAuthenticated && (
                <div className="relative md:mr-4">
                  <div 
                    className="relative"
                    onMouseEnter={() => setShowFeedbackDropdown(true)}
                    onMouseLeave={() => setShowFeedbackDropdown(false)}
                  >
                    <button
                      onClick={() => setShowFeedbackDropdown(!showFeedbackDropdown)}
                      className="inline-flex items-center px-1.5 md:px-2 py-1 text-xs md:text-sm font-medium rounded-md text-gray-700 hover:text-primary hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/50"
                      title="Help"
                    >
                      <MessageSquare className="w-4 h-4 md:mr-1" />
                      <span className="hidden md:inline">HELP</span>
                      <ChevronDown className="w-3 h-3 md:w-4 md:h-4 ml-1" />
                    </button>
                    
                    {showFeedbackDropdown && (
                      <div className="absolute right-0 top-full pt-2 w-48 z-50">
                        <div className="bg-white rounded-md shadow-lg py-1 border border-gray-200">
                          <button
                            onClick={() => openFeedbackModal('feedback')}
                            className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 text-left"
                          >
                            <MessageSquare className="h-4 w-4 mr-2" />
                            Send Feedback
                          </button>
                          <button
                            onClick={() => openFeedbackModal('bug')}
                            className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 text-left"
                          >
                            <AlertTriangle className="h-4 w-4 mr-2" />
                            Report Bug
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {isAuthenticated ? (
                <div className="flex items-center gap-2 md:gap-4">
                  <BackgroundJobPill
                    onOpenVerification={() => setShowVerificationModal(true)}
                    onOpenDMUnlock={openDMUnlockFromHeader}
                    searchId={searchId}
                  />

                  {loadingBalance ? (
                    <div className="animate-pulse h-4 w-32 bg-gray-200 rounded"></div>
                  ) : balance ? (
                    <button
                      onClick={handleBalanceClick}
                      title={balanceTooltip}
                      className="text-xs md:text-sm font-medium text-gray-700 hover:text-primary transition-colors flex items-center gap-2 md:gap-3"
                    >
                      <div className="flex items-center gap-1">
                        <Search className="h-3 w-3 md:h-3.5 md:w-3.5 text-blue-600" />
                        <span className="text-blue-600 font-semibold">{balance.searches_remaining}</span>
                        <span className="hidden md:inline text-xs text-gray-500">searches</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3 md:h-3.5 md:w-3.5 text-purple-600" />
                        <span className="text-purple-600 font-semibold">{balance.dm_unlocks_remaining}</span>
                        <span className="hidden md:inline text-xs text-gray-500">people</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Mail className="h-3 w-3 md:h-3.5 md:w-3.5 text-green-600" />
                        <span className="text-green-600 font-semibold">{balance.email_verifications_remaining}</span>
                        <span className="hidden md:inline text-xs text-gray-500">verifies</span>
                      </div>
                    </button>
                  ) : null}
                  
                  <div className="flex items-center relative">
                    <div 
                      className="relative"
                      onMouseEnter={() => setShowUserMenu(true)}
                      onMouseLeave={() => setShowUserMenu(false)}
                    >
                      <button 
                        onClick={() => setShowUserMenu(!showUserMenu)}
                        className="text-sm md:text-md text-gray-900 font-medium hover:text-primary px-2 md:px-3 py-2 rounded-md transition-colors flex items-center gap-1 md:gap-2 max-w-[120px] md:max-w-none"
                      >
                        <User className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" />
                        <span className="truncate">{user?.username}</span>
                      </button>
                      
                      {showUserMenu && (
                        <div className="absolute right-0 top-full pt-2 w-48 z-50">
                          <div className="bg-white rounded-md shadow-lg py-1 border border-gray-200">
                            <Link
                              to="/subscriptions"
                              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              onClick={() => setShowUserMenu(false)}
                            >
                              <CreditCard className="h-4 w-4 mr-2" />
                              Billing
                            </Link>
                            <Link
                              to="/settings"
                              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              onClick={() => setShowUserMenu(false)}
                            >
                              <Settings className="h-4 w-4 mr-2" />
                              Settings
                            </Link>
                            <button
                              onClick={handleLogout}
                              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 text-left"
                            >
                              <LogOut className="h-4 w-4 mr-2" />
                              Logout
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex space-x-2 md:space-x-4">
                  <Link
                    to="/login"
                    className="inline-flex items-center px-2 py-1 md:px-3 md:py-1.5 border border-transparent text-sm font-medium rounded-md text-primary hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/50"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="inline-flex items-center px-2 py-1 md:px-3 md:py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/50"
                  >
                    Register
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      
      <Suspense fallback={<div>Loading...</div>}>
        <FeedbackModal
          isOpen={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          type={feedbackType}
        />
      </Suspense>

      {showVerificationModal && (
        <Suspense fallback={<div>Loading...</div>}>
          <EmailVerificationModal
            isOpen={showVerificationModal}
            onClose={() => setShowVerificationModal(false)}
            personEmails={[]}
            placeEmails={[]}
            emailType="both"
            searchId={searchId}
            bulkMode={!!searchId}
            showCompletedOnOpen
          />
        </Suspense>
      )}

      {isAuthenticated && (
        <CompletionNotification
          previousBalance={previousBalance}
          currentBalance={balance}
          onViewDetails={() => setShowVerificationModal(true)}
          searchId={searchId}
        />
      )}

      {isAuthenticated && (
        <Suspense fallback={null}>
          <BulkDMUnlockModal
            isOpen={isDMUnlockReopenOpen}
            onClose={closeDMUnlockFromHeader}
            personIds={dmUnlockPersonIds}
            searchId={dmUnlockSearchId ?? undefined}
            reopenBatchId={reopenDMUnlockBatchId}
          />
        </Suspense>
      )}

      {isAuthenticated && <DMUnlockCompletionToast />}
    </>
  );
};

export default Header; 
